import { useState } from 'react';
import { Button } from '../ui/Button';
import { Input } from '../ui/Input';
import { Textarea } from '../ui/Textarea';
import { ImageUpload } from './ImageUpload';
import type { BlockType } from '../../lib/types';

interface AddBlockModalProps {
    isOpen: boolean;
    onClose: () => void;
    onSave: (block: {
        type: BlockType;
        title: string;
        url?: string;
        content?: string;
        imageFile?: File;
    }) => Promise<void>;
}

export function AddBlockModal({ isOpen, onClose, onSave }: AddBlockModalProps) {
    const [blockType, setBlockType] = useState<BlockType | null>(null);
    const [title, setTitle] = useState('');
    const [url, setUrl] = useState('');
    const [content, setContent] = useState('');
    const [imageFile, setImageFile] = useState<File | null>(null);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState('');

    const resetForm = () => {
        setBlockType(null);
        setTitle('');
        setUrl('');
        setContent('');
        setImageFile(null);
        setError('');
    };

    const handleClose = () => {
        resetForm();
        onClose();
    };

    const handleSave = async () => {
        if (!blockType) return;

        if (!title.trim()) {
            setError('Title is required');
            return;
        }

        if (blockType === 'link' && !url.trim()) {
            setError('URL is required for links');
            return;
        }

        setSaving(true);
        setError('');

        try {
            await onSave({
                type: blockType,
                title: title.trim(),
                url: blockType === 'link' ? url.trim() : undefined,
                content: blockType === 'post' ? content.trim() : undefined,
                imageFile: imageFile || undefined,
            });
            handleClose();
        } catch (err) {
            setError(err instanceof Error ? err.message : 'Failed to save block');
        } finally {
            setSaving(false);
        }
    };

    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
            {/* Backdrop */}
            <div
                className="absolute inset-0 bg-black/50 backdrop-blur-sm"
                onClick={handleClose}
            />

            {/* Modal */}
            <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-lg mx-4 max-h-[90vh] overflow-y-auto">
                <div className="p-6">
                    <h2 className="text-2xl font-bold mb-6">Add New Block</h2>

                    {error && (
                        <div className="bg-red-100 border-2 border-red-400 text-red-700 px-4 py-3 rounded-xl mb-4">
                            {error}
                        </div>
                    )}

                    {/* Block Type Selection */}
                    {!blockType && (
                        <div className="grid grid-cols-2 gap-4">
                            <button
                                onClick={() => setBlockType('link')}
                                className="p-6 border-2 border-gray-200 rounded-xl hover:border-primary hover:bg-primary/5 transition-all text-left group"
                            >
                                <div className="text-3xl mb-2">🔗</div>
                                <h3 className="font-bold text-lg">Link</h3>
                                <p className="text-gray-500 text-sm">Add a link to any website</p>
                            </button>

                            <button
                                onClick={() => setBlockType('post')}
                                className="p-6 border-2 border-gray-200 rounded-xl hover:border-primary hover:bg-primary/5 transition-all text-left group"
                            >
                                <div className="text-3xl mb-2">📝</div>
                                <h3 className="font-bold text-lg">Post</h3>
                                <p className="text-gray-500 text-sm">Create a post with image</p>
                            </button>
                        </div>
                    )}

                    {/* Link Form */}
                    {blockType === 'link' && (
                        <div className="space-y-4">
                            <button
                                onClick={() => setBlockType(null)}
                                className="text-sm text-gray-500 hover:text-gray-700 flex items-center gap-1"
                            >
                                ← Back to block types
                            </button>

                            <div>
                                <label htmlFor="link-title" className="block text-sm font-semibold text-gray-700 mb-2">
                                    Title*
                                </label>
                                <Input
                                    id="link-title"
                                    value={title}
                                    onChange={(e) => setTitle(e.target.value)}
                                    placeholder="My Website"
                                />
                            </div>

                            <div>
                                <label htmlFor="link-url" className="block text-sm font-semibold text-gray-700 mb-2">
                                    URL*
                                </label>
                                <Input
                                    id="link-url"
                                    type="url"
                                    value={url}
                                    onChange={(e) => setUrl(e.target.value)}
                                    placeholder="https://example.com"
                                />
                            </div>
                        </div>
                    )}

                    {/* Post Form */}
                    {blockType === 'post' && (
                        <div className="space-y-4">
                            <button
                                onClick={() => setBlockType(null)}
                                className="text-sm text-gray-500 hover:text-gray-700 flex items-center gap-1"
                            >
                                ← Back to block types
                            </button>

                            <div>
                                <label htmlFor="post-title" className="block text-sm font-semibold text-gray-700 mb-2">
                                    Title*
                                </label>
                                <Input
                                    id="post-title"
                                    value={title}
                                    onChange={(e) => setTitle(e.target.value)}
                                    placeholder="My Latest Artwork"
                                />
                            </div>

                            <div>
                                <label className="block text-sm font-semibold text-gray-700 mb-2">
                                    Image
                                </label>
                                <ImageUpload
                                    onImageSelect={setImageFile}
                                    isUploading={saving}
                                />
                            </div>

                            <div>
                                <label htmlFor="post-content" className="block text-sm font-semibold text-gray-700 mb-2">
                                    Description
                                </label>
                                <Textarea
                                    id="post-content"
                                    value={content}
                                    onChange={(e) => setContent(e.target.value)}
                                    placeholder="Write something about this post..."
                                    rows={3}
                                />
                            </div>
                        </div>
                    )}

                    {/* Actions */}
                    {blockType && (
                        <div className="flex gap-3 mt-6">
                            <Button
                                variant="ghost"
                                onClick={handleClose}
                                className="flex-1"
                                disabled={saving}
                            >
                                Cancel
                            </Button>
                            <Button
                                variant="primary"
                                onClick={handleSave}
                                className="flex-1"
                                disabled={saving}
                            >
                                {saving ? 'Saving...' : 'Add Block'}
                            </Button>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}
